import type {
    IPencilOfferInputs,
    IPencilOfferInputsIncentive,
    Offer,
    OfferSettings,
    OfferType,
    IDealerFee,
    PaymentTerm,
    TermOverrides
} from '@makemydeal/dr-platform-types';
import {
    ApplicationCodes,
    BALLOON,
    CASH,
    CashOptions,
    FINANCE,
    LEASE,
    PaymentScheduleTypes,
    RESIDUAL_DOLLAR
} from '@makemydeal/dr-platform-types';
import isEmpty from 'lodash.isempty';
import * as types from '../types/actionTypes';
import type { AnyAction } from 'redux';

import { PAYMENT_GRID_RESIDUAL_INTENT, REQUESTED_DOWN_PAYMENT_FIELD_NAME } from '../constants/meta';
import { ACQUISITION_FEE_ID, FeesCategoriesType, PAYMENT_REQUEST_OVERRIDE_NODE, RATE_TYPE } from '../constants/payment';
import type { ITerm } from '../types/paymentTypes';
import * as selectors from '../selectors';
import { getFeatureToggle } from '../selectors';
import type { LeaseInputUpdateOptions, LeaseInputUpdateValues, PendingLenderSync, PenciledInputsState } from '../types/commonTypes';
import { deriveAppliedIncentivesFromIds, updateOfferWithCashIncentives } from './cashIncentivesUtils';
import { isCashDealIncentivesFixesEnabled, isCashSwitchStandardRebateEnabled, parseIncentiveId } from './incentives';
import { isExpectedDownPayment } from './common';

import { isPrimaryPaymentOption, updatePaymentGridLoadingStatuses } from './paymentGridUtils';
import { convertRatesByType, termHasImplausibleMoneyFactor } from './rateUtils';
import {
    getDownPaymentItemForOfferType,
    getIsPreferredTermField,
    getOfferTypeTerms,
    parseSelectedTermMonths,
    pqResidualInfoOverrides,
    upqResidualInfoOverrides,
    validateSelectedTermWithIsPreferred,
    isPaymentGridOriginatedAction
} from './paymentUtils';
import { mergeTaxItems } from './taxUtils';
import { translateApplyMethodsToEquityAllocationMethod } from './equityAllocationUtils';

/**
 * Context type for payment received processing
 */
type PaymentReceivedContext = {
    payload: any;
    originalAction: any;
    originalActionType: string;
    useNewTermIdStandard: boolean;
    isManagerView: boolean;
    isSalesView: boolean;
    isSalesViewStartingCash: boolean;
    enableOverrideTaxFixes: boolean;
    enableGridSelectedTermTaxSync: boolean;
    isEquityAllocationOrderEnabled: boolean;
    useNewTermId: boolean;
    offerState: Offer | undefined;
    nonCash: boolean;
    isFlexiblePaymentsGrid: boolean;
    sourceApplication: string;
    isUserProgramQuotes: boolean;
    isPaymentGridRedesignEnabled: boolean;
    isSecurityDepositOverrideResetEnabled: boolean;
};

/**
 * Type for state update parameters
 */
type StateUpdateParams = {
    state: PenciledInputsState;
    action: any;
    offerType: OfferType;
    offer: Offer;
    settings: OfferSettings;
};

/**
 * Helper functions for updating state
 */
export const updateInputWithValue = (key: string, value: any, _offerType: OfferType = FINANCE, state: PenciledInputsState) => {
    const prevActiveOffer = state.byOfferType?.[_offerType];
    return {
        ...prevActiveOffer,
        ...{
            [key]: value,
            isUpdatedByUser: true
        }
    };
};

export const updateOfferTypeWithInputs = (inputs: any, _offerType: OfferType = FINANCE, state: PenciledInputsState) => {
    return {
        ...state.byOfferType,
        ...{
            [_offerType]: inputs
        }
    };
};

export const getUpdatedCashState = (existingCash: any, payload: any, isPaymentCallSuccessful: boolean): IPencilOfferInputs => {
    let updatedCash = {};
    if (isPaymentCallSuccessful && !isEmpty(existingCash) && !isEmpty(payload.cash)) {
        updatedCash = {
            ...existingCash,
            ...payload.cash
        };
    } else if (!isPaymentCallSuccessful && !isEmpty(existingCash)) {
        updatedCash = {
            ...existingCash,
            appliedIncentiveIds: [],
            appliedIncentives: [],
            dealerFees: [],
            dealerFeesTotal: 0,
            dmvFees: 0,
            fees: {
                total: 0,
                cappedFeeTotal: 0,
                upfrontFeeTotal: 0,
                fee: {
                    taxFee: []
                }
            },
            isUpdatedByUser: true,
            selectedIncentives: [],
            totalAmountDueAfterCredits: 0,
            totalAmountDueWithoutProducts: 0,
            totalTax: 0
        };
    }

    return updatedCash;
};

/**
 * 1. Extract context from action and offer
 */
const extractPaymentReceivedContext = (action: AnyAction, offer: Offer, offerType: OfferType): PaymentReceivedContext => {
    const payload = action.payload;
    const originalAction = action.meta?.originalAction || {};
    const originalActionType = originalAction.type;

    const useNewTermIdStandard = getFeatureToggle(offer, 'useNewTermIdStandard');
    const isManagerView = originalAction?.meta?.sourceApplication === ApplicationCodes.MV;
    const isSalesView = selectors.getIsSalesView(offer);
    // Deliberately separate from `isSalesView` above, which stays as-is: it feeds mergeTaxItems at three other
    // call sites, and it has always resolved false here because the offer node has no `dealer`/`widgetSettings`.
    // Reviving it would change tax-flag merging as a side effect, so Starting Cash gets its own flag (DE766119).
    const isSalesViewStartingCash = action.meta?.isSalesViewStartingCash === true;
    const enableOverrideTaxFixes = getFeatureToggle(offer, 'overrideTaxFixes_US1984109');
    const enableGridSelectedTermTaxSync = getFeatureToggle(offer, 'DE782944_Enable_Grid_Selected_Term_Tax_Sync');
    // Meta first: alternate scenario scopes have their offer featureToggles stripped (DE749297).
    const isEquityAllocationOrderEnabled =
        action.meta?.enableEquityAllocationOrder ?? getFeatureToggle(offer, 'enableEquityAllocationOrder');
    const useNewTermId = useNewTermIdStandard && isManagerView;
    const offerState = useNewTermId ? offer : undefined;
    const nonCash = offerType !== CASH;

    const isFlexiblePaymentsGrid = isPaymentGridOriginatedAction(originalAction);

    const sourceApplication = originalAction?.meta?.sourceApplication;
    const isUserProgramQuotes = selectors.getIsUserProgramQuotes({ offer });
    const isPaymentGridRedesignEnabled = selectors.isAspPaymentGridRedesignEnabled({ offer });
    const isSecurityDepositOverrideResetEnabled = !!getFeatureToggle(offer, 'DE784015_Enable_Security_Deposit_Override_Reset');

    return {
        payload,
        originalAction,
        originalActionType,
        useNewTermIdStandard,
        isManagerView,
        isSalesView,
        isSalesViewStartingCash,
        enableOverrideTaxFixes,
        enableGridSelectedTermTaxSync,
        isEquityAllocationOrderEnabled,
        useNewTermId,
        offerState,
        nonCash,
        isFlexiblePaymentsGrid,
        sourceApplication,
        isUserProgramQuotes,
        isPaymentGridRedesignEnabled,
        isSecurityDepositOverrideResetEnabled
    };
};

/**
 * 2. Initialize the new state structure
 */
const initializeNewState = (state: PenciledInputsState, offerType: OfferType): PenciledInputsState => {
    if (!state.byOfferType) return state;
    return {
        ...state,
        byOfferType: {
            ...state.byOfferType,
            [offerType]: { ...(state.byOfferType?.[offerType] || {}) }
        }
    };
};

/**
 * 3. Update term selection for non-cash offers
 */
const updateTermSelection = (
    newOfferInputs: IPencilOfferInputs,
    context: PaymentReceivedContext,
    state: PenciledInputsState,
    offerType: OfferType
): void => {
    const selectedTerm = state.byOfferType?.[offerType]?.term;
    // if (!selectedTerm) return;

    if (context.isFlexiblePaymentsGrid) newOfferInputs.term = selectedTerm;
    else {
        const validatedTerm = validateSelectedTermWithIsPreferred(offerType, selectedTerm, context.payload, context.offerState);
        if (validatedTerm !== null) newOfferInputs.term = validatedTerm;
        else newOfferInputs.term = undefined;
    }
};

/**
 * 4. Update basic offer type fields from payload
 */
const updateOfferTypeFields = (newOfferInputs: any, payload: any, offerType: OfferType): void => {
    const offerTypePayload = payload[offerType];

    if (offerTypePayload) {
        newOfferInputs.netSellingPrice = offerTypePayload.netSellingPrice;
        // Assigned even when absent, so a balance that is removed stops being reported and stops adjusting.
        newOfferInputs.priorLeaseBalance = offerTypePayload.priorLeaseBalance;
        newOfferInputs.priorLeaseBalanceMethod = offerTypePayload.priorLeaseBalanceMethod;
        newOfferInputs.estTotalPurchasePrice = offerTypePayload.estTotalPurchasePrice;
        newOfferInputs.registrationType = offerTypePayload.registrationType;
        newOfferInputs.registrationExpiresOn = offerTypePayload.registrationExpiresOn;
        // Equity allocation method is only applicable for lease deals (F245767)
        if (offerType === LEASE) {
            newOfferInputs.equityAllocationMethod = offerTypePayload.equityAllocationMethod;
            newOfferInputs.tradeApplyMethod = offerTypePayload.tradeApplyMethod;
            newOfferInputs.rebateApplyMethod = offerTypePayload.rebateApplyMethod;
            newOfferInputs.dealerCashApplyMethod = offerTypePayload.dealerCashApplyMethod;
            newOfferInputs.cashApplyMethod = offerTypePayload.cashApplyMethod;
            newOfferInputs.excessAmount = offerTypePayload.excessAmount;
            newOfferInputs.excessMileRate = offerTypePayload.excessMileRate;
            newOfferInputs.inceptionRate = offerTypePayload.inceptionRate;
            newOfferInputs.initialMileageCharge = offerTypePayload.initialMileageCharge;
            newOfferInputs.initialMilesCharged = offerTypePayload.initialMilesCharged;
            newOfferInputs.retroMiles = offerTypePayload.retroMiles;
            newOfferInputs.secondPaymentDueDate = offerTypePayload.secondPaymentDueDate;
            newOfferInputs.standardAllowedMiles = offerTypePayload.standardAllowedMiles;
            newOfferInputs.thresholdMiles = offerTypePayload.thresholdMiles;
            newOfferInputs.totalExcessMilesPurchased = offerTypePayload.totalExcessMilesPurchased;
            newOfferInputs.totalMiles = offerTypePayload.totalMiles;
            newOfferInputs.excessAmount = offerTypePayload.excessAmount;
            newOfferInputs.excessMileageCharge = offerTypePayload.excessMileageCharge;
            newOfferInputs.retroMilesThreshold = offerTypePayload.retroMilesThreshold;
            newOfferInputs.initialMilesThreshold = offerTypePayload.initialMilesThreshold;
            newOfferInputs.standardMiles = offerTypePayload.standardMiles;
        }
    }
};

export const getLeaseInputTermUpdate = ({
    term,
    offerType,
    oldValue = 0,
    newValue = 0,
    isManagerView = false
}: LeaseInputUpdateOptions): LeaseInputUpdateValues | undefined => {
    // Determine if term update is needed: for Manager View, any value change triggers update;
    // for other applications, only update when old value is below new value minus threshold
    const expectedDownPayment = isExpectedDownPayment(oldValue, newValue);
    const shouldUpdate = isManagerView ? oldValue !== newValue : expectedDownPayment;
    const parsedTermMonth = parseSelectedTermMonths(term);

    if (!shouldUpdate || !parsedTermMonth) {
        return undefined;
    }
    const termId = selectors.getTermId(offerType, parsedTermMonth, newValue);

    if (expectedDownPayment) {
        // Legacy logic. Must refactor.
        return {
            term: termId, // Switching the termId can lead to penciledInputs <-> Terms ID disconnection
            downPayment: newValue
        };
    }

    return { downPayment: newValue };
};

/**
 * Response term ids embed the *requested* cash down, and the grid path takes the stored id unvalidated — so once
 * Starting Cash moves off the id's `_downpayment_` extension the primary payment arrives under an id the store
 * does not hold. Fall back to months: a primary payment prices one term per month at the deal's own cash down,
 * so a single match is that term; ambiguity is left alone rather than guessed.
 */
const resolvePrimaryPaymentTerm = (context: PaymentReceivedContext, termId: string, offerType: OfferType) => {
    const terms = context.payload?.terms;
    const exactMatch = terms?.byId?.[termId];
    if (exactMatch) return exactMatch as PaymentTerm;

    const months = parseSelectedTermMonths(termId);
    if (months == null) return undefined;

    const sameMonths = (terms?.[`${offerType}Terms`] || []).filter((id: string) => parseSelectedTermMonths(id) === months);
    return sameMonths.length === 1 ? (terms?.byId?.[sameMonths[0]] as PaymentTerm) : undefined;
};

/**
 * 5. Handle down payment adjustments for lease with due at signing deduction
 */

const handleDownPaymentAdjustment = (
    newOfferInputs: IPencilOfferInputs,
    context: PaymentReceivedContext,
    state: PenciledInputsState,
    settings: OfferSettings,
    offerType: OfferType
): void => {
    const { sourceApplication, isFlexiblePaymentsGrid, isEquityAllocationOrderEnabled, isSalesViewStartingCash } = context;
    const isManagerView = sourceApplication === ApplicationCodes.MV;
    const isShopperPlatform = sourceApplication === ApplicationCodes.SP;

    // In MV and Sales View the lease down payment is the user-owned "Starting Cash": re-synced only to the PS
    // echo, never to a calculated amount. Keyed on the Sales View flag, not SP, since AMD is also SP and
    // keeps the legacy deduct behaviour.
    const ownsStartingCash = (isManagerView || isSalesViewStartingCash) && isEquityAllocationOrderEnabled;

    if (ownsStartingCash) {
        if (!newOfferInputs.term) return;
        if (isPrimaryPaymentOption(context.originalAction)) {
            const { startingCash } = resolvePrimaryPaymentTerm(context, newOfferInputs.term, offerType) ?? {};
            if (typeof startingCash === 'number') {
                newOfferInputs.downPayment = startingCash;
            }
        }
        return;
    }

    if (settings.isDueAtSigninAmountDeduct && !ownsStartingCash) {
        if (!newOfferInputs.term) return;
        if (!isPrimaryPaymentOption(context.originalAction)) return;
        const termData = context.payload.terms.byId[newOfferInputs.term!];
        const oldValue = state.byOfferType?.[LEASE]?.downPayment;
        const paymentFrequency = context.payload?.[LEASE]?.paymentFrequency;
        const isOnePayLease = paymentFrequency && paymentFrequency === PaymentScheduleTypes.OnePayLease;
        const newValue = isOnePayLease ? termData?.netCashDown : termData?.dueAtSigning;

        if (isOnePayLease) {
            newOfferInputs.downPayment = newValue;
            return;
        }

        const updatedInputs = getLeaseInputTermUpdate({
            term: newOfferInputs.term,
            offerType,
            oldValue,
            newValue,
            isManagerView
        });

        if (updatedInputs != null) {
            Object.assign(newOfferInputs, updatedInputs);
        }
    } else if (isShopperPlatform && !isFlexiblePaymentsGrid) {
        const getDownPaymentItem = (term: ITerm) =>
            getDownPaymentItemForOfferType(offerType, term, REQUESTED_DOWN_PAYMENT_FIELD_NAME, {
                useResolutionLogic: false
            });
        const termMonths = getIsPreferredTermField(offerType, context.payload.terms, 'months');
        const downPayment = getIsPreferredTermField(offerType, context.payload.terms, getDownPaymentItem) as number;

        const newTermId = termMonths !== null && termMonths > 0 ? selectors.getTermId(offerType, termMonths, downPayment) : null;
        newOfferInputs.term = newTermId ?? newOfferInputs.term;
    }
};

/**
 * The price the dealer set, recovered from the price Payment Services calculated by taking off everything
 * it added. A missing cash offset counts as nothing added: the Orchestrator reports it on lease but not on
 * finance, and `undefined` here would put NaN into the price the next request sends.
 */
const getBasePriceFromPayment = (offerTypePayload: any): number =>
    offerTypePayload.vehicleSalePrice - (offerTypePayload.cashOffsetAmountAddedToPrice ?? 0);

/**
 * 6. Handle lease-specific pricing updates
 */
const updateLeasePricing = (
    newOfferInputs: IPencilOfferInputs,
    payload: any,
    state: PenciledInputsState,
    isEquityAllocationOrderEnabled: boolean
): void => {
    newOfferInputs.offerPrice = getBasePriceFromPayment(payload.lease);
    newOfferInputs.vehicleSalePrice = payload.lease.vehicleSalePrice;
    /*
     * The property requested is different from the response
     * request has the order vs response contains the status
     */

    if (state.byOfferType?.[LEASE]?.capitalizeFirstPayment !== undefined || payload.lease.firstPaymentCapitalized) {
        newOfferInputs.capitalizeFirstPayment = payload.lease.firstPaymentCapitalized;
    }

    newOfferInputs.isTargetOutOfPocket = isEquityAllocationOrderEnabled
        ? payload.lease.isTargetCashAmountPaid ?? false
        : payload.lease.cashOption === CashOptions.AddCashOffsetToPrice;
    newOfferInputs.cashOffsetAmountAddedToPrice = payload.lease.cashOffsetAmountAddedToPrice;
};

/**
 * 7. Handle residual overrides for lease
 */
const handleResidualOverrides = (
    newOfferInputs: IPencilOfferInputs,
    context: PaymentReceivedContext,
    action: AnyAction,
    offerType: OfferType,
    state: PenciledInputsState
): void => {
    const overridesActions = [
        types.UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE,
        types.UPDATED_BASE_RESIDUAL_PERCENT_OVERRIDE,
        types.UPDATED_ADJUSTED_RESIDUAL_DOLLAR_OVERRIDE,
        types.UPDATED_ADJUSTED_RESIDUAL_PERCENT_OVERRIDE,
        types.UPDATED_BALLOON_PAYMENT_AMOUNT
    ];

    const hasLeaseTerm = context.payload.terms.leaseTerms?.length;
    const hasBalloonTerm = context.payload.terms.balloonTerms?.length;
    const isBreakdownEdit = isResidualBreakdownProgramResidualEdit(context);
    const hasAction = overridesActions.includes(context.originalActionType) || isBreakdownEdit;

    // Same rule as the rate unit (DE762937): these read the SELECTED term's record off the response, and a
    // request narrowed to one grid column never asked Payment Services to price that term - so the residual
    // it answers with is regenerated, not the one the deal is priced from.
    if (isRequestNarrowedPastSelectedTerm(context, offerType, newOfferInputs.term)) return;

    if ((hasLeaseTerm || hasBalloonTerm) && hasAction) {
        const residualOverrides = context.isUserProgramQuotes
            ? upqResidualInfoOverrides(action, newOfferInputs.term)
            : pqResidualInfoOverrides(state, action, offerType);
        Object.keys(residualOverrides).forEach((key) => {
            const value = residualOverrides[key as keyof typeof residualOverrides];
            if (isBreakdownEdit && value == null) return;
            (newOfferInputs as any)[key] = value;
        });
    }
};

/**
 * The acquisition fee is re-seeded from the response only when the action that triggered the payment
 * can have changed the lender program: applying a program from the Programs screen, or (DE754192) a
 * term selection that the reducer flagged as an actual lender change. The payment-grid toggle alone
 * is not enough — it is true for every grid click, so gating on it would append a fee entry to deals
 * whose lender never changed and pin that amount into every later request.
 */
const isProgramDerivedAcqFeeSource = (context: PaymentReceivedContext, newOfferInputs: IPencilOfferInputs): boolean => {
    if (context.sourceApplication !== ApplicationCodes.MV) return false;
    if (context.originalAction.type === types.APPLIED_PROGRAM) return true;

    return (
        context.originalAction.type === types.SELECTED_TERM &&
        Boolean((newOfferInputs as IPencilOfferInputs & PendingLenderSync).pendingLenderProgramSync)
    );
};

/**
 * 8. Handle acquisition fee override for applied program in manager view
 */
const handleAcquisitionFeeOverride = (
    newState: any,
    context: PaymentReceivedContext,
    action: AnyAction,
    newOfferInputs: IPencilOfferInputs
): void => {
    if (isProgramDerivedAcqFeeSource(context, newOfferInputs)) {
        const terms = action.payload?.terms;
        const dealersFees = terms?.byId?.[newOfferInputs.term!]?.dealerFees;
        const leaseFeesOverrides = newState.byOfferType?.lease?.feesOverride;
        const acqFee = dealersFees?.find(
            (fee: any) => fee?.dealerFeeTypeCode === ACQUISITION_FEE_ID && fee?.originalCategory === FeesCategoriesType.LENDER
        );

        // one-shot marker: clear it whether or not the response carried a lender acquisition fee,
        // so a response without one can't leave it armed for an unrelated later payment
        (newState.byOfferType.lease as IPencilOfferInputs & PendingLenderSync).pendingLenderProgramSync = undefined;

        if (acqFee) {
            // both slots, kept in step: acqFeeOverride feeds overrides.acqFee when enableLenderFeeEdits
            // is off, and the capitalization flag must describe the same fee (cf. handleUPQProgramFallback)
            newState.byOfferType.lease.acqFeeOverride = acqFee.dealerFeeAmount;
            newState.byOfferType.lease.acqFeeUpFrontOverride = !acqFee.dealerFeeCapped;

            const newAcqFeeEntry = {
                feeTypeId: acqFee.dealerFeeTypeCode,
                feeAmount: acqFee.dealerFeeAmount,
                reqCapIndicator: acqFee.dealerFeeCapped,
                feeDescription: acqFee.dealerFeeDesc,
                feeName: acqFee.dealerFeeName,
                category: acqFee.originalCategory,
                includeInCalcIndicator: acqFee.includedInCalc
            };

            if (!leaseFeesOverrides || leaseFeesOverrides.length === 0) {
                newState.byOfferType.lease.feesOverride = [newAcqFeeEntry];
            } else {
                // acqFee source is already filtered to LENDER category, so the replacement is category-safe
                const existingIdx = leaseFeesOverrides.findIndex((fee: any) => fee?.feeTypeId === ACQUISITION_FEE_ID);
                if (existingIdx !== -1) {
                    const updated = [...leaseFeesOverrides];
                    updated[existingIdx] = newAcqFeeEntry;
                    newState.byOfferType.lease.feesOverride = updated;
                } else {
                    newState.byOfferType.lease.feesOverride = [...leaseFeesOverrides, newAcqFeeEntry];
                }
            }
        }
    }
};

const toCents = (value: unknown): number => Math.round((Number(value) || 0) * 100);

export const keepPricedAppliedIncentiveIds = (
    responseIds: number[],
    previousIds: number[] | undefined,
    pricedAmount: unknown,
    catalog: { conditionals: IPencilOfferInputsIncentive[]; nonConditionals: IPencilOfferInputsIncentive[] }
): number[] => {
    if (responseIds.length > 0 || !previousIds?.length || toCents(pricedAmount) <= 0) return responseIds;
    const previousIdSet = new Set(previousIds);
    const catalogIncentives = [...catalog.conditionals, ...catalog.nonConditionals];
    const previousCents = catalogIncentives
        .filter((incentive) => previousIdSet.has(parseIncentiveId(incentive.id)))
        .reduce((sum, incentive) => sum + toCents(incentive.amount), 0);
    return previousCents === toCents(pricedAmount) ? previousIds : responseIds;
};

/**
 * 9. Handle cash offer updates
 */
const handleCashOfferUpdates = (
    newState: any,
    context: PaymentReceivedContext,
    offer: Offer,
    newOfferInputs: IPencilOfferInputs,
    state: PenciledInputsState
): void => {
    const isPaymentCallSuccessful = context.payload[CASH]?.success;
    newState.byOfferType[CASH] = getUpdatedCashState(state.byOfferType?.[CASH], context.payload, isPaymentCallSuccessful);

    const extendedOffer = { ...offer, ...newState };
    extendedOffer.incentives = context.payload.incentives;

    try {
        const updatedCashIncentives = updateOfferWithCashIncentives(extendedOffer, context.payload);

        // Not every cash payment recalc requests incentives (e.g. toggling an incentive checkbox
        // dispatches UPDATE_SELECTED_INCENTIVES, which isn't a "program catalog" action, so
        // includeIncentives resolves to false and the response's incentives node is empty). Without
        // this guard, that response would wipe out the catalog a prior includeIncentives:true call had
        // populated. The same responses also come back with an empty appliedIncentives breakdown
        // despite a correct appliedIncentiveIds - protected separately below. See DE759607/US2076398.
        if (isCashDealIncentivesFixesEnabled(offer)) {
            // No ?? fallback needed here (unlike previousIncentives below): updateOfferWithCashIncentives
            // always returns incentives.conditionals/nonConditionals as arrays (never undefined), so
            // ?.length alone is sufficient - undefined > 0 is false either way.
            const hasNewCatalog =
                updatedCashIncentives.incentives?.conditionals?.length > 0 ||
                updatedCashIncentives.incentives?.nonConditionals?.length > 0;
            const previousIncentives = state.byOfferType?.[CASH]?.incentives;
            const hasPreviousCatalog =
                (previousIncentives?.conditionals?.length ?? 0) > 0 || (previousIncentives?.nonConditionals?.length ?? 0) > 0;
            if (!hasNewCatalog && previousIncentives && hasPreviousCatalog) {
                updatedCashIncentives.incentives = previousIncentives;
            }

            if (isCashSwitchStandardRebateEnabled(offer)) {
                updatedCashIncentives.appliedIncentiveIds = keepPricedAppliedIncentiveIds(
                    updatedCashIncentives.appliedIncentiveIds,
                    state.byOfferType?.[CASH]?.appliedIncentiveIds,
                    context.payload[CASH]?.appliedIncentiveAmount,
                    updatedCashIncentives.incentives
                );
            }

            // The same includeIncentives:false responses above also come back with an empty
            // appliedIncentives.conditionals/nonConditionals breakdown, even when appliedIncentiveIds
            // correctly lists what this exact response applied. A response can also report the
            // breakdown for only one bucket (e.g. standards only) while still omitting an id that's
            // genuinely applied - checking "is either bucket non-empty" would then skip the rebuild
            // entirely and under-report the total. Reconcile per id instead: derive only the ids
            // missing from both buckets, and merge them in rather than replacing the response's own
            // breakdown outright.
            // AppliedIncentives.conditionals/nonConditionals are both required arrays, and
            // updateOfferWithCashIncentives always defaults appliedIncentiveIds to [], so neither needs
            // an extra fallback here beyond existingBreakdown's own default.
            const existingBreakdown = updatedCashIncentives.appliedIncentives ?? { conditionals: [], nonConditionals: [] };
            const accountedForIds = new Set(
                [...existingBreakdown.conditionals, ...existingBreakdown.nonConditionals].map(parseIncentiveId)
            );
            const missingAppliedIncentiveIds = updatedCashIncentives.appliedIncentiveIds.filter(
                (id: number) => !accountedForIds.has(id)
            );
            if (missingAppliedIncentiveIds.length > 0) {
                const derived = deriveAppliedIncentivesFromIds(missingAppliedIncentiveIds, updatedCashIncentives.incentives);
                updatedCashIncentives.appliedIncentives = {
                    conditionals: [...existingBreakdown.conditionals, ...derived.conditionals],
                    nonConditionals: [...existingBreakdown.nonConditionals, ...derived.nonConditionals]
                };
            }
        }

        // Merge payment tax items with store to not lose manual tax flags
        const currentTaxItems = newOfferInputs.taxItems || [];
        const paymentTaxItems = context.payload[CASH]?.taxItems || [];

        newState.byOfferType[CASH] = {
            ...newState.byOfferType[CASH],
            ...updatedCashIncentives,
            taxItems: mergeTaxItems(
                currentTaxItems,
                paymentTaxItems,
                context.isManagerView,
                context.isSalesView,
                context.enableOverrideTaxFixes
            )
        };
    } catch {
        // Handle case where updateOfferWithCashIncentives might fail due to missing data structure
        const currentTaxItems = newOfferInputs.taxItems || [];
        const paymentTaxItems = context.payload[CASH]?.taxItems || [];

        newState.byOfferType[CASH] = {
            ...newState.byOfferType[CASH],
            taxItems: mergeTaxItems(
                currentTaxItems,
                paymentTaxItems,
                context.isManagerView,
                context.isSalesView,
                context.enableOverrideTaxFixes
            )
        };
    }
};

/**
 * 10. Update total incentives for finance and lease
 */
const updateTotalIncentives = (
    newOfferInputs: IPencilOfferInputs,
    offer: Offer,
    newState: any,
    offerType: OfferType,
    payloadTerms?: any
): void => {
    const extendedOffer = { ...offer, ...newState };

    if (payloadTerms?.byId && offerType !== CASH) {
        extendedOffer.terms = {
            ...extendedOffer.terms,
            byId: { ...(extendedOffer.terms?.byId || {}), ...payloadTerms.byId }
        };
    }

    try {
        if (offerType === FINANCE) {
            newOfferInputs.totalIncentives = selectors.findAllIncentiveTotalFromOffer(extendedOffer, FINANCE);
        }

        if (offerType === LEASE) {
            newOfferInputs.totalIncentives = selectors.findAllIncentiveTotalFromOffer(extendedOffer, LEASE);
        }
    } catch (error) {
        // Handle case where selectors might fail due to missing data structure
        newOfferInputs.totalIncentives = 0;
    }
};

/**
 * 11. Handle payment grid updates
 */
const handlePaymentGridUpdates = (
    newState: any,
    context: PaymentReceivedContext,
    action: AnyAction,
    offerType: OfferType
): void => {
    const paymentGrid = context.originalAction?.meta?.paymentGrid;

    if (paymentGrid?.gridAction && !paymentGrid?.isFlexBuyPayment) {
        const paymentGridData = newState.byOfferType[offerType].paymentGridData;
        newState.byOfferType[offerType].paymentGridData = updatePaymentGridLoadingStatuses(
            offerType,
            paymentGridData,
            action,
            false
        );
    }
};

/**
 * 12. Handle tax item updates for non-cash primary payments
 */
const handleTaxItemUpdates = (
    newState: any,
    context: PaymentReceivedContext,
    state: PenciledInputsState,
    offerType: OfferType,
    newOfferInputs: IPencilOfferInputs
) => {
    const payloadTerm = context.payload?.terms?.byId[newOfferInputs.term!];
    // TODO DE782944: remove `isPrimaryPaymentOption` from this check together with the
    // DE782944_Enable_Grid_Selected_Term_Tax_Sync toggle - `payloadTerm` and the narrowed-request check limit the sync
    // to the selected term.
    const syncsFromThisPayment =
        isPrimaryPaymentOption(context.originalAction) ||
        (context.enableGridSelectedTermTaxSync && !isRequestNarrowedPastSelectedTerm(context, offerType, newOfferInputs.term));

    if (offerType !== CASH && syncsFromThisPayment && payloadTerm) {
        const previousTaxItems = state?.byOfferType?.[offerType]?.taxItems || [];
        const updatedTaxItems = payloadTerm?.taxItems || [];

        newState.byOfferType[offerType].taxItems = mergeTaxItems(
            previousTaxItems,
            updatedTaxItems,
            context.isManagerView,
            context.isSalesView,
            context.enableOverrideTaxFixes
        );
        newState.byOfferType[offerType].taxOverrideUpfront = !payloadTerm.isTaxCapped;
    }
};

/**
 * 13. Handle UPQ program fallback overrides when required fields are missing
 */
export const handleUPQProgramFallback = (
    newOfferInputs: IPencilOfferInputs,
    context: PaymentReceivedContext,
    offerType: OfferType
): void => {
    if (!context.originalAction.meta?.missingRequiredUPQFields) return;
    if (!newOfferInputs.term) return;

    // A narrowed grid request is answered for the column it named, not for the deal's selected term, so
    // re-seeding the deal from that term's regenerated record cannot help price the column - it only replaces
    // what the user entered, and it forces both residual types to dollar, discarding a percent residual typed
    // into the grid (DE762937).
    if (isRequestNarrowedPastSelectedTerm(context, offerType, newOfferInputs.term)) return;

    // A grid residual edit is not a request to re-seed the deal: every field here comes from the response's
    // selected term, so it would move the Sell Rate cell the user never touched and replace the residual they just
    // typed with the lender's - in dollars, since the write forces both residual types to dollar.
    if (isPaymentGridResidualEdit(context)) return;

    const termData = context.payload.terms.byId?.[newOfferInputs.term];
    if (!termData) return;

    if (!termHasImplausibleMoneyFactor(termData, offerType)) {
        newOfferInputs.buyRateOverride = termData.buyRate;
        newOfferInputs.sellRateOverride = termData.sellRate;
        newOfferInputs.rateTypeOverride = termData.rateType;
    }

    if (offerType === LEASE) {
        newOfferInputs.adjustedResidualAmountOverride = termData.adjustedResidualValue;
        newOfferInputs.adjustedResidualPercentOverride = termData.adjustedResidualPercent;
        newOfferInputs.baseResidualAmountOverride = termData.baseResidualDollar;
        newOfferInputs.baseResidualPercentOverride = termData.baseResidualPercent;
        newOfferInputs.programResidualOverride = termData.programResidual;
        newOfferInputs.programResidualPercentOverride = termData.programResidualPercent;
        newOfferInputs.adjustedResidualTypeOverride = RESIDUAL_DOLLAR;
        newOfferInputs.baseResidualTypeOverride = RESIDUAL_DOLLAR;
        newOfferInputs.mileageChargeOverride = termData.excessMileageCharge ?? 0;
        newOfferInputs.securityDepositOverride = termData.securityDeposit ?? 0;

        const acquisitionFee = selectors.getDealerAcquisitionFeeFromTerm(termData);
        if (acquisitionFee != null) {
            const { dealerFeeAmount, capped } = acquisitionFee;
            newOfferInputs.acqFeeOverride = dealerFeeAmount;
            newOfferInputs.acqFeeUpFrontOverride = !capped;
        }

        if (termData.dealerFees && Array.isArray(termData.dealerFees)) {
            newOfferInputs.feesOverride = termData.dealerFees.map((fee: IDealerFee) => ({
                feeTypeId: fee.dealerFeeTypeCode,
                feeAmount: fee.dealerFeeAmount,
                reqCapIndicator: fee.dealerFeeCapped,
                feeDescription: fee.dealerFeeDesc,
                feeName: fee.dealerFeeName,
                category: fee.originalCategory,
                includeInCalcIndicator: fee.includedInCalc
            }));
        }
    }
};

export const syncSecurityDepositOverrideWithResponse = (
    newOfferInputs: IPencilOfferInputs,
    context: PaymentReceivedContext,
    offerType: OfferType
): void => {
    if (!context.isSecurityDepositOverrideResetEnabled) return;
    if (offerType !== LEASE) return;
    if (newOfferInputs.securityDepositOverride == null) return;
    if (!newOfferInputs.term) return;

    if (isRequestNarrowedPastSelectedTerm(context, offerType, newOfferInputs.term)) return;

    const respondedDeposit = context.payload?.terms?.byId?.[newOfferInputs.term]?.securityDeposit;
    if (respondedDeposit == null) return;

    newOfferInputs.securityDepositOverride = respondedDeposit;
};

const backfillEquityAllocationOrder = (newOfferInputs: any, context: PaymentReceivedContext): void => {
    if (!context.isEquityAllocationOrderEnabled || newOfferInputs.equityAllocationMethod) return;
    const equityAllocationMethod = translateApplyMethodsToEquityAllocationMethod(
        newOfferInputs.cashApplyMethod,
        newOfferInputs.tradeApplyMethod,
        newOfferInputs.rebateApplyMethod
    );
    if (equityAllocationMethod) newOfferInputs.equityAllocationMethod = equityAllocationMethod;
};

/**
 * Whether the request this response answers asked about the deal's selected term at all.
 *
 * Read off the request node the call actually went out with, not from grid state: the same response is
 * processed after the grid has moved on, and only the request can say which terms it named. An empty or absent
 * list is a request that named no terms - a deal-screen payload - and names the selected term by default.
 */
const isRequestNarrowedPastSelectedTerm = (
    context: PaymentReceivedContext,
    offerType: OfferType,
    termId: string | undefined
): boolean => {
    if (!context.isPaymentGridRedesignEnabled) return false;

    const termList = context.originalAction?.meta?.[PAYMENT_REQUEST_OVERRIDE_NODE]?.[offerType]?.termList;
    if (!Array.isArray(termList) || !termList.length) return false;

    const selectedTermMonths = parseSelectedTermMonths(termId!);
    if (selectedTermMonths == null) return false;

    return !termList.includes(selectedTermMonths);
};

/**
 * Whether this response answers a residual the user typed into, or cleared from, the payment grid.
 *
 * The grid's two inputs are independent: a residual edit is not a statement about the rate, so the rate the
 * response happens to come back with must not overwrite the one on screen. Unticking Auto Select makes that
 * visible - the deal moves to user-program-quotes, the response carries the engine's own rate, and a residual edit
 * then moved the Sell Rate cell the user had not touched (DE762937).
 *
 * Keyed on the grid's own intent, so a Residual Breakdown tab edit keeps DE747870's sync exactly as it is.
 */
const isPaymentGridResidualEdit = (context: PaymentReceivedContext): boolean => {
    if (!context.isPaymentGridRedesignEnabled) return false;
    if (context.originalAction?.meta?.userIntent !== PAYMENT_GRID_RESIDUAL_INTENT) return false;

    return (
        context.originalActionType === types.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE ||
        context.originalActionType === types.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE ||
        context.originalActionType === types.REMOVED_PROGRAM_RESIDUAL_OVERRIDE
    );
};

const isResidualBreakdownProgramResidualEdit = (context: PaymentReceivedContext): boolean => {
    if (!context.isPaymentGridRedesignEnabled) return false;
    if (isPaymentGridResidualEdit(context)) return false;

    return (
        context.originalActionType === types.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE ||
        context.originalActionType === types.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE
    );
};

/**
 * 14. DE747870: Sync the lease rate override to the byId term's rate type/values from the PO response, so the
 * grid cell's label and value always reflect the latest payment (e.g. "MF 6.24000"). Mirrors handleUPQProgramFallback.
 */
const reconcileLeaseRateOverrideType = (
    newOfferInputs: IPencilOfferInputs,
    context: PaymentReceivedContext,
    offerType: OfferType
): void => {
    if (offerType !== LEASE) return;

    const termData = context.payload.terms.byId[newOfferInputs.term!];
    const termRateType = termData?.rateType;
    if (!termRateType) return;

    // DE762937: a grid residual or sell-rate edit narrows the request to the column it touched, so the SELECTED
    // term is not one Payment Services was asked to price - the record it answers with is regenerated, in the
    // lease-native unit. Adopting that flipped the deal's rate unit, and with it every grid column's label,
    // from the APR the request carried to MoneyFactor - against rate figures still on the APR scale, which the
    // next request then sent back out mislabelled.
    //
    // The request states the unit; the response cannot change it behind the request's back. A full-grid, cell
    // selection or deal-screen response still names the selected term, so DE747870's sync is unchanged there.
    if (isRequestNarrowedPastSelectedTerm(context, offerType, newOfferInputs.term)) return;

    // A residual edit says nothing about the rate, so it may not move one - including on a request that DOES name
    // the selected term, which is the shape a residual edit on the deal's own column takes.
    if (isPaymentGridResidualEdit(context)) return;

    if (termHasImplausibleMoneyFactor(termData, offerType)) return;

    newOfferInputs.buyRateOverride = termData.buyRate;
    newOfferInputs.sellRateOverride = termData.sellRate;
    newOfferInputs.rateTypeOverride = termRateType;

    const months = parseSelectedTermMonths(newOfferInputs.term!);
    const existingTermOverride = months != null ? newOfferInputs.overridesByTerm?.[months] : undefined;
    if (!existingTermOverride) return;

    // US2075533: step 15 stamps a `rateType` onto every priced term, so an entry no longer proves the user
    // overrode this term's rates. Mirroring into a stamp-only entry would invent a per-term sell rate override
    // that then pins the rate against program and lender changes. Only refresh entries that hold real rates.
    const holdsRateOverride = existingTermOverride.sellRate != null || existingTermOverride.buyRate != null;
    if (context.isPaymentGridRedesignEnabled && !holdsRateOverride) return;

    newOfferInputs.overridesByTerm = {
        ...newOfferInputs.overridesByTerm,
        [months!]: {
            ...existingTermOverride,
            sellRate: termData.sellRate,
            buyRate: termData.buyRate,
            // The values just written are the response's, so they are in the response's unit by definition — the
            // stamp has to move with them. Leaving a stale one made step 14 convert an already-converted value.
            ...(existingTermOverride.rateType != null && { rateType: termRateType })
        }
    };
};

/**
 * 15. US2075533: keep every priced term's own rate type alongside that term's rates, so the payment grid's
 * per-column sell rate label follows the program that priced that column instead of inheriting the selected
 * term's rate type from the offer-level `rateTypeOverride`.
 *
 * NOTE: `overridesByTerm` is keyed by months, so it cannot hold a distinct rate type per Ford FlexBuy percent
 * variant — one response can return several term ids sharing the same `months` (see `paymentGridMiddleware`'s
 * flexBuyTerms/flexBuyPercents). Those columns therefore share one stamp, the last one written.
 */
const stampRateTypesByTerm = (
    newOfferInputs: IPencilOfferInputs,
    context: PaymentReceivedContext,
    offerType: OfferType,
    isThePrimaryPaymentOption: boolean
): void => {
    if (!context.isPaymentGridRedesignEnabled || offerType === CASH) return;

    const terms = context.payload?.terms;
    if (!terms) return;
    const termIds = getOfferTypeTerms(offerType, terms) as string[] | undefined;
    if (!termIds?.length) return;

    const prevOverridesByTerm = newOfferInputs.overridesByTerm || {};
    // The unit an unstamped stored value was captured in: it predates this story, so it is in whatever the deal
    // was quoting at the time. Read before step 15 re-pins it from the response — hence the ordering there.
    const dealRateType = newOfferInputs.rateTypeOverride;
    const retypedTerms: Record<string | number, TermOverrides> = {};
    const clearedTerms: (string | number)[] = [];

    termIds.forEach((termId) => {
        const term = terms.byId?.[termId];
        const months = term?.months;
        if (months == null) return;

        const previous = prevOverridesByTerm[months];
        const rateType = term?.rateType;
        const holdsRate = previous?.sellRate != null || previous?.buyRate != null;

        // `RateType` also permits 'InterestRate', which neither `RateTypeMap` (blank label) nor
        // `convertRatesByType` (treats anything but MoneyFactor as APR) handles. Leave those terms alone rather
        // than stamp a type that would mislabel and mis-convert the value.
        if (rateType && rateType !== RATE_TYPE.APR && rateType !== RATE_TYPE.MoneyFactor) return;

        // A term the response prices without a rate type must not keep a stale stamp outranking the offer-level
        // `rateTypeOverride` — but only a response-authored stamp is stale. When the entry also holds a rate the
        // user typed, the stamp is the unit of *that* number, and dropping it would relabel their value.
        if (!rateType) {
            if (previous?.rateType == null || holdsRate) return;
            const { rateType: _dropped, ...withoutStamp } = previous;
            if (!Object.keys(withoutStamp).length) clearedTerms.push(months);
            else retypedTerms[months] = withoutStamp;
            return;
        }

        // Only rewrite what actually changed, so an unchanged grid keeps its object identity.
        if (previous?.rateType === rateType) return;

        // Retyping a stored value without converting it would render the user's number in the wrong unit
        // ("MF 5.9") — the mismatch DE747870 fixed for the selected term. `rateHandlers` resolves the source
        // unit the same way, so the two conversion sites agree on what an unstamped value is in.
        // Only *conversion* needs the primary-payment guard, not the stamp. A regular grid answers every term
        // once per cash-down row, so converting on each response would multiply and then divide the user's
        // value; writing the same rate type repeatedly is idempotent. Gating the whole step stopped a
        // mixed-rate-type grid from ever being labelled, which is the bug this story exists to fix.
        const sourceRateType = previous?.rateType ?? dealRateType;
        const needsConversion = holdsRate && !!sourceRateType && sourceRateType !== rateType;

        // Relabelling without converting would render the user's number in the wrong unit ("APR 5.9"), so a
        // non-primary response leaves such a term completely alone rather than stamping half the change.
        if (needsConversion && !isThePrimaryPaymentOption) return;

        if (needsConversion) {
            const { transformedBuyRate, transformedSellRate } = convertRatesByType(
                rateType,
                previous.buyRate ?? null,
                previous.sellRate ?? null
            );
            retypedTerms[months] = { ...previous, buyRate: transformedBuyRate, sellRate: transformedSellRate, rateType };
            return;
        }

        retypedTerms[months] = { ...previous, rateType };
    });

    if (!Object.keys(retypedTerms).length && !clearedTerms.length) return;

    const updatedOverridesByTerm = { ...prevOverridesByTerm, ...retypedTerms };
    // A retype from one term id must outrank a clear from another sharing the same `months` (the FlexBuy case
    // above), or the column silently loses a rate type the response did supply.
    clearedTerms.filter((months) => !(months in retypedTerms)).forEach((months) => delete updatedOverridesByTerm[months]);
    newOfferInputs.overridesByTerm = updatedOverridesByTerm;
};

/**
 * Main handler function for PAYMENT_RECEIVED action
 * Orchestrates all the smaller update functions
 */
export const handlePaymentReceived = (params: StateUpdateParams): PenciledInputsState => {
    const { state, action, offerType, offer, settings } = params;

    // 1. Extract context from action and offer
    const context = extractPaymentReceivedContext(action, offer, offerType);

    // 2. Initialize the new state structure
    const newState = initializeNewState(state, offerType);
    if (!newState.byOfferType) return newState;
    const newOfferInputs = newState.byOfferType?.[offerType];

    // Process non-cash offers
    if (context.nonCash) {
        // 3. Update term selection for non-cash offers
        updateTermSelection(newOfferInputs, context, state, offerType);

        // 4. Update basic offer type fields from payload
        updateOfferTypeFields(newOfferInputs, context.payload, offerType);

        // Handle lease-specific updates
        if (offerType === LEASE) {
            // 5. Handle down payment adjustments for lease with due at signing deduction
            handleDownPaymentAdjustment(newOfferInputs, context, state, settings, offerType);

            // 6. Handle lease-specific pricing updates
            updateLeasePricing(newOfferInputs, context.payload, state, context.isEquityAllocationOrderEnabled);

            // 6b. US1997527 — backfill equity allocation order from apply methods for legacy deals
            backfillEquityAllocationOrder(newOfferInputs, context);

            // 7. Handle residual overrides for lease
            handleResidualOverrides(newOfferInputs, context, action, offerType, state);

            // 8. Handle acquisition fee override for applied program in manager view
            handleAcquisitionFeeOverride(newState, context, action, newOfferInputs);

            // 9. Sync MRM overrides from PO response
            //
            // The cap governs the residual, and it is read off the SELECTED term - so a request narrowed to one
            // grid column would replace the deal's cap with whatever Payment Services regenerated for a term it
            // was not asked to price (DE762937).
            const termData = isRequestNarrowedPastSelectedTerm(context, offerType, newOfferInputs.term)
                ? undefined
                : context.payload?.terms?.byId?.[newOfferInputs.term!];
            if (termData) {
                newOfferInputs.maxResidualizedMsrp = termData.maxResidualizedMsrp;
                newOfferInputs.maxResidualizedMsrpApplied = termData.maxResidualizedMsrpApplied;
            }
        }
        if (offerType === BALLOON) {
            newOfferInputs.balloonType = context.payload?.terms?.byId[newOfferInputs.term!]?.balloonType;
            newOfferInputs.balloonTerm = context.payload?.terms?.byId[newOfferInputs.term!]?.balloonTerm;
            handleResidualOverrides(newOfferInputs, context, action, offerType, state);
        }
    }

    // Handle cash offer updates
    if (offerType === CASH) {
        // 9. Handle cash offer updates
        handleCashOfferUpdates(newState, context, offer, newOfferInputs, state);
    }

    // 10. Update total incentives for finance and lease
    updateTotalIncentives(newOfferInputs, offer, newState, offerType, context.payload?.terms);

    // 11. Handle payment grid updates
    handlePaymentGridUpdates(newState, context, action, offerType);

    // 12. Handle tax item updates for non-cash primary payments
    handleTaxItemUpdates(newState, context, state, offerType, newOfferInputs);

    // 13. Handle UPQ program fallback overrides when required fields are missing
    handleUPQProgramFallback(newOfferInputs, context, offerType);
    syncSecurityDepositOverrideWithResponse(newOfferInputs, context, offerType);

    // 14. US2075533: Store each priced term's own rate type so grid columns keep their own label.
    // MUST run before step 15: it converts a stored value out of the unit it was captured in, which it reads
    // from the deal's pre-response `rateTypeOverride`. Step 15 overwrites both that and the selected term's
    // stored rates, so running it first would leave this converting values that are already in the new unit.
    const gridPayment = context.originalAction?.meta?.paymentGrid;
    stampRateTypesByTerm(newOfferInputs, context, offerType, !gridPayment?.gridAction || !!gridPayment?.isMainPayment);

    // 15. DE747870: Sync lease rate override to the PO response term rate type/values
    reconcileLeaseRateOverrideType(newOfferInputs, context, offerType);

    return newState;
};
